/**
 * Rotation disk gizmo for an OpeningAnnotation in phase-1.
 *
 * A `THREE.RingGeometry` mesh anchored at the opening center, lying flat
 * in the XY plane (rotation around world +Z — i.e. yaw in floor view).
 * The ring scales to a constant screen-space size by reading the camera
 * + canvas size on each viewer `update` event, mirroring
 * `SphereMarker.updateScale`.
 *
 * Drag math (floor view):
 *   - Compute the cursor's angle around the opening center (atan2 in
 *     world XY).
 *   - Delta angle = currentAngle - dragStartAngle.
 *   - When Shift is held, snap the cumulative angle (from drag start) to
 *     15° increments.
 *   - Rotate the frame's `u` by the delta around `+Z`; rebuild polygon.
 *
 * Live preview via `OpeningAnnotation.setPoints`; the parent controller
 * pushes an `OpeningGeometrySnapshot` on drag end.
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';
import { Utils } from '../../../../utils';
import { PixelHitTester } from '../selection/PixelHitTester';
import { OpeningAnnotation } from '../../../../utils/PointoramaAnnotation';
import { OpeningFrame, rebuildPolygonWithU } from './OpeningFrame';

/** Ring inner/outer radii in screen pixels — kept constant via `updateScale`. */
const RING_INNER_PX = 130;
const RING_OUTER_PX = 150;

/** Shift-snap step in radians (15°). */
const SNAP_STEP_RAD = (15 * Math.PI) / 180;

/** Ring color: cyan accent, distinct from translate handles. */
const RING_COLOR = 0x00ddff;

export class OpeningRotationHandle {
  private mesh: THREE.Mesh;
  private material: THREE.MeshBasicMaterial;
  private boundUpdateScale: () => void;

  /** Frozen polygon at drag start — the baseline each frame rotates from. */
  private dragBeforePoints: THREE.Vector3[] | null = null;

  /** Frame snapshot at drag start. Used so the rotation builds on the
   *  pre-drag `u`, not a continuously-rotating live `u` (which would
   *  drift). */
  private dragStartFrame: OpeningFrame | null = null;

  /** Angle of the cursor around the opening center at drag start. */
  private dragStartAngle = 0;

  constructor(
    private readonly viewer: Viewer,
    parentGroup: THREE.Group,
    private readonly hitTester: PixelHitTester,
  ) {
    // Geometry built with placeholder radii — `updateScale` rebuilds it
    // on every frame to keep the ring at constant pixel size. RingGeometry
    // doesn't support live-radius mutation cheaply, so we set
    // mesh.scale.set(s,s,s) per frame instead and pick radii (1, 1.36)
    // that match the 28/38 pixel ratio.
    const geom = new THREE.RingGeometry(1.0, RING_OUTER_PX / RING_INNER_PX, 48);
    this.material = new THREE.MeshBasicMaterial({
      color: RING_COLOR,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.6,
      depthTest: false,
      depthWrite: false,
    });
    this.mesh = new THREE.Mesh(geom, this.material);
    // Ring lies flat in XY (lying horizontally for the top-down view).
    // RingGeometry is authored in the XY plane by default, so no rotation
    // needed.
    this.mesh.renderOrder = 999;
    parentGroup.add(this.mesh);

    this.boundUpdateScale = () => this.updateScale();
    this.viewer.addEventListener('update', this.boundUpdateScale);
    this.updateScale();
  }

  /** Update ring center to follow the frame's center (in helpers-local coords). */
  refresh(frame: OpeningFrame, helpersGroupPosition: THREE.Vector3): void {
    // Helpers group is positioned at the opening center already, so
    // local position is zero in the common case. Compute defensively
    // in case the group hasn't been repositioned yet this frame.
    this.mesh.position.copy(frame.center).sub(helpersGroupPosition);
    this.mesh.visible = true;
  }

  setVisible(visible: boolean): void {
    this.mesh.visible = visible;
  }

  /**
   * Hit test: cursor must be within the ring's annulus (in pixel space).
   * Returns true if hit (the rotation handle has no "side" so payload is
   * unit-typed).
   */
  pickAt(event: MouseEvent, frame: OpeningFrame): boolean {
    const mousePx = this.hitTester.mousePx(event);
    const centerPx = this.hitTester.projectWorldToPixel({ x: frame.center.x, y: frame.center.y, z: frame.center.z });
    if (!centerPx) return false;
    const d = centerPx.distanceTo(mousePx);
    return d >= RING_INNER_PX && d <= RING_OUTER_PX;
  }

  /**
   * Start a drag. Snapshots the polygon + frame so subsequent
   * `onMouseMove` calls rotate from a fixed baseline (otherwise the
   * accumulated rotation would feedback through the frame each frame
   * and cause jitter).
   */
  beginDrag(event: MouseEvent, annotation: OpeningAnnotation, frame: OpeningFrame): void {
    this.dragBeforePoints = annotation.points.map((p) => p.position.clone());
    this.dragStartFrame = {
      center: frame.center.clone(),
      u: frame.u.clone(),
      v: frame.v.clone(),
      normal: frame.normal.clone(),
      halfWidth: frame.halfWidth,
      halfHeight: frame.halfHeight,
    };
    this.dragStartAngle = this.cursorAngleAroundCenter(event, frame.center);
  }

  /**
   * Rotate the opening by the cursor's current angle delta. Shift snaps
   * the cumulative angle to 15° steps.
   */
  onMouseMove(event: MouseEvent, annotation: OpeningAnnotation): void {
    if (!this.dragBeforePoints || !this.dragStartFrame) return;
    const currentAngle = this.cursorAngleAroundCenter(event, this.dragStartFrame.center);
    let delta = wrapToPi(currentAngle - this.dragStartAngle);
    if (event.shiftKey) {
      delta = Math.round(delta / SNAP_STEP_RAD) * SNAP_STEP_RAD;
    }
    // Rotate the start-frame's u around +Z (yaw in floor view) by delta.
    const cos = Math.cos(delta);
    const sin = Math.sin(delta);
    const startU = this.dragStartFrame.u;
    const newU = new THREE.Vector3(startU.x * cos - startU.y * sin, startU.x * sin + startU.y * cos, 0);
    // Reuse `rebuildPolygonWithU` for consistency with the align actions —
    // it preserves the start-frame center, v axis, and dimensions.
    const newPoints = rebuildPolygonWithU(this.dragStartFrame, newU);
    annotation.setPoints(newPoints.map((position) => ({ position, pointCloudId: '' })));
  }

  /**
   * Finish the drag. Returns `{ before, after }` for the controller to
   * push an `OpeningGeometrySnapshot`.
   */
  endDrag(annotation: OpeningAnnotation): { before: THREE.Vector3[]; after: THREE.Vector3[] } | null {
    if (!this.dragBeforePoints) return null;
    const before = this.dragBeforePoints;
    const after = annotation.points.map((p) => p.position.clone());
    this.dragBeforePoints = null;
    this.dragStartFrame = null;
    return { before, after };
  }

  dispose(): void {
    this.viewer.removeEventListener('update', this.boundUpdateScale);
    this.mesh.geometry.dispose();
    this.material.dispose();
    this.mesh.parent?.remove(this.mesh);
  }

  /**
   * Scale the ring so its inner radius reads as `RING_INNER_PX` pixels
   * on screen. Mirrors `SphereMarker.updateScale`.
   */
  private updateScale(): void {
    const camera = this.viewer.scene.getActiveCamera();
    const size = this.viewer.renderer.getSize(new THREE.Vector2());
    const worldCenter = new THREE.Vector3();
    this.mesh.getWorldPosition(worldCenter);
    const distance = camera.position.distanceTo(worldCenter);
    const projected = Utils.projectedRadius(1, camera, distance, size.width, size.height);
    if (projected < 1e-6) return;
    const s = RING_INNER_PX / projected;
    this.mesh.scale.set(s, s, s);
  }

  /** Angle (radians) of `event`'s cursor around `center` in world XY. */
  private cursorAngleAroundCenter(event: MouseEvent, center: THREE.Vector3): number {
    // Unproject the cursor onto the floor Z = center.z plane. For an
    // ortho top-down view, this is essentially a screen → world XY
    // conversion at that Z.
    const camera = this.viewer.scene.getActiveCamera();
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, camera);
    const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -center.z);
    const hit = ray.ray.intersectPlane(plane, new THREE.Vector3());
    if (!hit) return 0;
    return Math.atan2(hit.y - center.y, hit.x - center.x);
  }
}

/** Wrap `a` to (-π, π]. */
function wrapToPi(a: number): number {
  while (a > Math.PI) a -= 2 * Math.PI;
  while (a <= -Math.PI) a += 2 * Math.PI;
  return a;
}
